// src/pages/BlogPage.js
import React from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Divider,
} from "@mui/material";

function truncate(text = "", n = 120) {
  if (text.length <= n) return text;
  return text.slice(0, n).trimEnd() + "…";
}

function BlogCard({ title, description, href, host, date }) {
  // Use provided description, otherwise a short placeholder
  const fullText =
    description && description.length > 0
      ? description
      : "Description is not available.";

  const snippet = truncate(fullText, 140);

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardActionArea
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textAlign: "left", p: 0 }}
      >
        <CardContent>
          <Typography variant="h6">{title}</Typography>

          {host && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              {host} {date ? `• ${date}` : ""}
            </Typography>
          )}

          {/* Snippet + Read more hint */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, lineHeight: 1.5 }}
          >
            {snippet}{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                ml: 0.5,
              }}
            >
              Read more →
            </Typography>
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function BlogPage() {
  const blogsByMe = [
    {
      title: "Fine-tuning Large Language Models",
      description: "Fine-tuning Large Language Models for specific tasks.",
      href: "https://medium.com/@thiru42/fine-tuning-large-language-models-e245fa36c6cc",
      host: "Medium",
      date: "April 2026",
    },
    {
      title: "How the Transformer has Evolved",
      description: "A look at the evolution of Transformer architectures since the original 2017 paper by Google.",
      href: "https://medium.com/@thirupathi.thangavel/how-the-transformer-has-evolved-since-googles-2017-paper-25e6292334d9",
      host: "Medium",
      date: "Jan 2026",
    },
    {
      title: "Rotary Positional Embeddings",
      description: "Improving Transformer Architecture with Rotary Positional Embeddings",
      href: "https://www.linkedin.com/pulse/improving-transformer-architecture-rotary-positional-thangavel/",
      host: "LinkedIn",
      date: "Sep 2023",
    },
    {
      title: "BERT vs GPT Comparison",
      description: "Comparison of Bidirectional Encoder Representations from Transformers (BERT) vs Generative Pre-training Transformer (GPT) models.",
      href: "https://medium.com/@thirupathi.thangavel/bert-vs-gpt-comparison-a93037913cdc",
      host: "Medium",
      date: "Sep 2023",
    },
  ];

  const recommendedBlogs = [
    {
      title: "Complete RAG System",
      description: "Complete Retrieval-Augmented Generation (RAG) System Explained",
      href: "https://jamwithai.substack.com/p/the-complete-rag-system",
      host: "Substack",
      date: "Sep 2025",
    },
    {
      title: "Evaluating LLM Systems",
      description: "Metrics, Challenges, and Best Practices for Evaluating Large Language Model (LLM) Systems",
      href: "https://medium.com/data-science-at-microsoft/evaluating-llm-systems-metrics-challenges-and-best-practices-664ac25be7e5",
      host: "Medium",
      date: "Mar 2024",
    }, 
    {
      title: "Neural Network Optimizers Explained",
      description: "A Visual Explanation of Gradient Descent Methods: Momentum, AdaGrad, RMSProp, Adam",
      href: "https://towardsdatascience.com/a-visual-explanation-of-gradient-descent-methods-momentum-adagrad-rmsprop-adam-f898b102325c/",
      host: "Towards Data Science",
      date: "Jan 2020",
    },
    {
      title: "Geosharded Recommendations",
      description: "Sharding Approach for Geosharded Recommendations",
      href: "https://medium.com/tinder/geosharded-recommendations-part-1-sharding-approach-d5d54e0ec77a",
      host: "Medium",
      date: "May 2019",
    },
  ];

  return (
    <Container maxWidth={false} sx={{ display: "flex", justifyContent: "center" }}>
      <Box sx={{ width: "100%", maxWidth: 700 }}>
        <Typography variant="h4">
          Blog posts
        </Typography>

        {blogsByMe.map((b) => (
          <BlogCard key={b.href} {...b} />
        ))}

        <Divider sx={{ mb: 2 }} />
        <Box sx={{ mt: 5 }}>
          <Typography variant="h4" gutterBottom>
            Recommended
          </Typography>
        </Box>

        {recommendedBlogs.map((b) => (
          <BlogCard key={b.href} {...b} />
        ))}
      </Box>
    </Container>
  );
}
